import React from 'react';
import { Alert, Button, Empty, Table, Tabs } from 'antd';
import { DollarOutlined, ImportOutlined, MinusCircleOutlined, StopOutlined } from '@ant-design/icons';
import { SmartBetaStatus, SmartBetaView } from '../../../services/smartBetaApi';
import { formatNumber } from '../../../utils/numberFormat';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { targetWeights } from '../smartBetaForm';
import { Card, Row, Stat, Summary } from '../styles';
import RebalancePanel from './RebalancePanel';
import LogViewer from '../../../components/LogViewer';

interface Props {
  saved: SmartBetaView;
  /** The portfolio the logs are read for. */
  portfolio: { id: string; portfolioId: string; portfolioName: string } | null;
  status: SmartBetaStatus | null;
  running: boolean;
  controlBusy: boolean;
  /** Unsaved edits above: money and runs wait for them. */
  blockedReason: string | null;
  onDeposit: () => void;
  onWithdraw: () => void;
  onImport: () => void;
  onEnd: () => void;
  onCancelPending: () => void;
  onStart: () => void;
  onStop: () => void;
}

/** Step 3 of either flow: the money in the strategy, the bot, and what each ticker holds. */
const FundAndRun: React.FC<Props> = ({
  saved,
  portfolio,
  status,
  running,
  controlBusy,
  blockedReason,
  onDeposit,
  onWithdraw,
  onImport,
  onEnd,
  onCancelPending,
  onStart,
  onStop,
}) => {
  const isMobile = useIsMobile();
  const capital = Number(saved.totalCapital);
  const idle = Math.max(Number(saved.idleCash), 0);
  const pending = Number(saved.pendingWithdrawal);
  const weights = targetWeights(saved.settings);
  // The running bot's log, else the last run's.
  const logExecution = status?.execution?.id ?? status?.lastExecution?.id ?? null;
  const weightOf = (s: string) => weights.find((w) => w.symbol === s)?.weight ?? 0;
  const startBlocked =
    blockedReason ?? (capital <= 0 && !saved.wallets.some((w) => Number(w.heldQty) > 0) ? 'Deposit first' : null);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? 12 : 16,
      }}
    >
      <Card aria-label="Capital">
        <Summary>
          <Stat>
            <div className="label">Capital</div>
            <div className="value">{formatNumber(capital)} VND</div>
          </Stat>
          <Stat>
            <div className="label">Idle cash</div>
            <div className="value">{formatNumber(idle)} VND</div>
          </Stat>
          <Stat>
            <div className="label">Next rebalance</div>
            <div className="value">{saved.nextRebalance ?? '—'}</div>
          </Stat>
        </Summary>
        <Row style={{ marginTop: 14, marginBottom: 0 }}>
          <Button
            type="primary"
            icon={<DollarOutlined />}
            onClick={onDeposit}
            disabled={running || !!blockedReason}
            title={running ? 'Wait for the rebalance to stop' : (blockedReason ?? undefined)}
          >
            Deposit
          </Button>
          <Button
            icon={<MinusCircleOutlined />}
            onClick={onWithdraw}
            disabled={running || capital <= 0}
            title={running ? 'Wait for the rebalance to stop' : undefined}
          >
            Withdraw
          </Button>
          <Button icon={<ImportOutlined />} onClick={onImport} disabled={running || !!saved.ending}>
            Import from Not trading
          </Button>
          <Button
            danger
            icon={<StopOutlined />}
            onClick={onEnd}
            disabled={running || !!saved.ending || saved.wallets.length === 0}
            style={{ marginLeft: 'auto' }}
          >
            End
          </Button>
        </Row>
        {saved.ending && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
            message="Ending: the bot is selling everything"
            description="The smart beta closes once it stops; lots it could not sell move to Not trading."
          />
        )}
        {saved.endedAt && saved.wallets.length === 0 && (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 12 }}
            message={`Ended on ${saved.endedAt.slice(0, 10)}`}
            description="Its shares are in Not trading on your Portfolio page. Deposit or import to start again."
          />
        )}
        {pending > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
            message={`${formatNumber(pending)} VND withdrawal waiting to be sold for`}
            description={
              running
                ? 'The bot is selling for it now; it is paid out once the run stops.'
                : `It is sold at the next rebalance${saved.nextRebalance ? ` (${saved.nextRebalance})` : ''}, or now with Rebalance now, then paid out.`
            }
            action={
              !running && (
                <Button size="small" onClick={onCancelPending}>
                  Cancel
                </Button>
              )
            }
          />
        )}
      </Card>

      <RebalancePanel
        status={status}
        nextRebalance={saved.nextRebalance}
        startBlockedReason={startBlocked}
        busy={controlBusy}
        onStart={onStart}
        onStop={onStop}
      />

      <Card aria-label="Allocation and logs">
        <Tabs
          size="small"
          destroyInactiveTabPane
          items={[
            {
              key: 'allocation',
              label: 'Allocation',
              children:
                saved.wallets.length > 0 ? (
                  <Table
                    size="small"
                    rowKey="instrumentConfigId"
                    pagination={false}
                    scroll={{ x: true }}
                    dataSource={saved.wallets}
                    columns={[
                      { title: 'Ticker', dataIndex: 'symbol' },
                      {
                        title: 'Target',
                        render: (_, w) => `${weightOf(w.symbol)}%`,
                        align: 'right',
                      },
                      {
                        title: 'Capital (VND)',
                        dataIndex: 'currentCapital',
                        align: 'right',
                        render: (v: string) => formatNumber(Number(v)),
                      },
                      ...(isMobile
                        ? []
                        : [
                            {
                              title: 'Position cost (VND)',
                              dataIndex: 'holdingsCost',
                              align: 'right' as const,
                              render: (v: string) => formatNumber(Number(v)),
                            },
                            {
                              title: 'Qty',
                              dataIndex: 'heldQty',
                              align: 'right' as const,
                              render: (v: string) => formatNumber(Number(v)),
                            },
                          ]),
                    ]}
                  />
                ) : (
                  <Empty description="Nothing allocated yet" />
                ),
            },
            {
              key: 'logs',
              label: 'Logs',
              children: logExecution ? (
                <div style={{ height: isMobile ? 360 : 420 }}>
                  <LogViewer
                    selectedPortfolio={portfolio}
                    strategyType="SMART_BETA"
                    executionId={logExecution}
                    compact
                    showExecutionControls={false}
                  />
                </div>
              ) : (
                <Empty description="No run yet — the bot's log shows here once it runs" />
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default FundAndRun;
